import type { z } from 'zod'

import { schemaModel } from '@scalar-org/entities/utility'

// ---------------------------------------------------------------------------

export type APIResponse<T> = {
  status: number
  data: T
  error: false
}

export type APIError = {
  status: number
  message: string
  error: true
  originalError: any
}

/** Create a standardized API return object */
function formatApiResponse<T>(data: T, status: number): APIResponse<T> {
  return {
    status,
    data,
    error: false,
  }
}

/** Create a standardized API Error object */
function formatApiError(
  message: string,
  status: number,
  error: any = null,
): APIError {
  return {
    status,
    message,
    error: true,
    originalError: error,
  }
}

// ---------------------------------------------------------------------------

export type RequestConfig<T> = Omit<RequestInit, 'body'> & {
  url: string
  schema: z.ZodType<T>
  disableAuth?: boolean // Require explicit flag to disable auth
  headers?: Record<string, string>
  data?: Record<string, any> | FormData | ReadableStream | null
  // PATCH must be upper case: https://github.com/JakeChampion/fetch/issues/254
  method:
    | 'delete'
    | 'get'
    | 'head'
    | 'options'
    | 'patch'
    | 'post'
    | 'put'
    | 'trace'
  stream?: boolean
}

/** Service to submit an API requests */
export function requestServiceFactory(
  baseUrl: string,
  getAuthToken?: () => string,
) {
  async function request<T>(
    args: RequestConfig<T> & { token?: string; stream: true },
  ): Promise<APIError | ReadableStream<Uint8Array>>
  async function request<T>(
    args: RequestConfig<T> & { token?: string },
  ): Promise<APIError | APIResponse<T>>
  async function request<T>({
    disableAuth = false,
    url: requestUrl,
    schema,
    data,
    stream,
    ...config
  }: RequestConfig<T> & { token?: string }) {
    // Normalize headers
    const headers: { [x: string]: string } = {}

    for (const headerKey in config.headers) {
      if (Object.hasOwn(config.headers, headerKey)) {
        headers[headerKey.toLowerCase()] =
          config.headers[headerKey].toLowerCase()
      }
    }

    // Default to JSON content if not specified
    if (!('content-type' in headers) && data)
      headers['content-type'] = 'application/json'

    // Must let the browser set content header for form data
    if (data instanceof FormData) delete headers['content-type']

    // Add the bearer auth unless disabled
    if (!disableAuth && (getAuthToken || config.token)) {
      const token = getAuthToken ? getAuthToken() : config.token
      if (!token)
        console.warn('WARNING: Auth token function returned an empty value')

      headers.authorization = `Bearer ${token}`
    }

    // Allow overriding of the base URL. Remove duplicate slashes
    const url = (
      requestUrl.includes('http') ? requestUrl : `${baseUrl}/${requestUrl}`
    ).replace(/([^:]\/)\/+/g, '$1')

    const method = config.method === 'patch' ? 'PATCH' : config.method

    // Execute the fetch and handle all errors
    return fetch(url, {
      ...config,
      method,
      body:
        data instanceof FormData ||
        data instanceof ReadableStream ||
        data === undefined
          ? data
          : JSON.stringify(data),
      headers,
      ...(data instanceof ReadableStream && {
        duplex: 'half',
      }),
    })
      .then(async (response) => {
        const isJson = response.headers
          .get('content-type')
          ?.includes('application/json')

        // For bad status codes we parse the response
        if (!response.ok) {
          const message = isJson
            ? String(
                (
                  await response
                    .json()
                    .catch(() => ({ message: 'Invalid response' }))
                ).message,
              ) || 'Unknown Error'
            : await response.text().catch(() => 'Invalid response')
          return formatApiError(message, response.status)
        }

        if (stream) return response.body ?? new ReadableStream()

        // Validate the response data and return
        try {
          // Get the response data
          const baseData =
            response.ok && isJson
              ? await response.json().catch(() => {
                  console.error(
                    `Invalid JSON response from API service from ${url}`,
                  )
                  return null
                })
              : await response.text()

          // When a schema is provided we thrown an error on validation failure
          const resultData = schema
            ? schemaModel(baseData, schema, true)
            : baseData

          return formatApiResponse(resultData, response.status)
        } catch (err) {
          console.error(err)
          console.error(`Response Zod validation error for ${url}`)
          // Generic message when response validation fails
          return formatApiError(
            `Invalid response data from endpoint: ${url}`,
            500,
          )
        }
      })
      .catch((err) => {
        // Catch and log any unexpected errors
        console.error(err)
        return formatApiError('Unknown Error', 500)
      })
  }

  return {
    baseUrl,
    getAuthToken,
    request,
  }
}

export type RequestService = ReturnType<typeof requestServiceFactory>
